import React, { useEffect, useState } from "react";
import {
    FormNumericInput,
    FormSwitch,
    FormTextArea,
    FormSelect,
    FormHeading,
    FormHelperText,
} from "components/form-fields";
import { ButtonSave } from "components/buttons";
import { ShowHide } from "components/layout";
import { LicenceCheckingService } from "services";
import { Intent } from "@blueprintjs/core";
import {
    AlertUnsavedData,
    NotificationToaster,
} from "components/notifications";
import { useDirtyData } from "hooks/useDirtyData";
import "./GreyFleetGeneral.css";
import { useValidation } from "hooks/useValidation";

export function GreyFleetGeneral() {
    const badRequestCode = 400;
    const errorMessageForSave = "Could not save settings. Please try again.";
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [rawGreyFleetSettings, setRawGreyFleetSettings] = useState({});
    const [greyFleetSettings, setGreyFleetSettings] = useState({
        vehiclesPerEmployee: 1,
        employeesCanRegisterVehicles: true,
        maximumPointsThreshold: 0,
        declarationRequired: false,
        declarationText: "",
        baseLicenceCheckFrequency: 1,
        checkFrequencyType: "D",
    });
    const [isValid, errors, validate] = useValidation();
    const isDataDirty = useDirtyData(rawGreyFleetSettings, greyFleetSettings);

    useEffect(checkUserValues, [greyFleetSettings, isValid, isLoading]);

    function checkUserValues() {
        const rules = [
            { fieldName: "vehiclesPerEmployee", required: true, minValue: 1 },
            {
                fieldName: "baseLicenceCheckFrequency",
                required: true,
                minValue: 1,
            },
            {
                fieldName: "maximumPointsThreshold",
                required: true,
                minValue: 0,
            },
            {
                fieldName: "checkFrequencyType",
                required: true,
                allowedValues: ["D", "W", "M"],
            },
        ];

        if (greyFleetSettings.declarationRequired) {
            rules.push({ fieldName: "declarationText", required: true });
        }

        validate(rules, greyFleetSettings, []);
    }

    function handleSave() {
        setIsSaving(true);

        LicenceCheckingService.saveGreyFleetSettings(greyFleetSettings)
            .then(
                () => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Settings saved successfully."
                    );
                    setRawGreyFleetSettings(greyFleetSettings);
                },
                (error) => {
                    if (error.response.status === badRequestCode) {
                        NotificationToaster.show(Intent.WARNING, error.response.data);
                    } else {
                        NotificationToaster.show(Intent.DANGER, errorMessageForSave);
                    }
                }
            )
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, errorMessageForSave);
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    useEffect(() => {
        LicenceCheckingService.getGreyFleetSettings()
            .then(
                (settings) => {
                    setRawGreyFleetSettings(settings);
                    setGreyFleetSettings(settings);
                },
                () => {
                   NotificationToaster.show(
                        Intent.DANGER,
                        "Could not load settings. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not load settings. Please try again."
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // Generic handler for numeric and switch changes
    function handleSettingsChange(field, value, options = {}) {
        setGreyFleetSettings((prev) => {
            // Special case for declarationRequired to clear declarationText if unchecked
            if (field === "declarationRequired") {
                return {
                    ...prev,
                    declarationRequired: value,
                    declarationText: value ? prev.declarationText : "",
                };
            }
            return {
                ...prev,
                [field]: value,
            };
        });
    }

    function handleDeclarationTextChange(event) {
        setGreyFleetSettings((prev) => ({
            ...prev,
            declarationText: event.target.value,
        }));
    }

    return (
        <div className="row">
            <h2>Settings</h2>
            <div className="form-field inline-item">
                <FormHeading headingLevel={"h4"}>
                    Base licence check frequency for grey fleet drivers
                </FormHeading>
                <FormHelperText>
                    This will apply to drivers who don't already fall into a standard licence checking policy
                </FormHelperText>
                <div className="grey-fleet-frequency-spacing">
                    <FormNumericInput
                        min={1}
                        max={1000}
                        selectedValue={
                            greyFleetSettings.baseLicenceCheckFrequency
                        }
                        onValueChange={(value) =>
                            handleSettingsChange(
                                "baseLicenceCheckFrequency",
                                value
                            )
                        }
                        dangerHelperText={errors.baseLicenceCheckFrequency}
                    />
                    <FormSelect
                        selectedValue={greyFleetSettings.checkFrequencyType}
                        onItemSelect={(unit) =>
                            handleSettingsChange("checkFrequencyType", unit.id)
                        }
                        items={[
                            { id: "D", name: "Days" },
                            { id: "W", name: "Weeks" },
                            { id: "M", name: "Months" },
                        ]}
                    />
                </div>
            </div>
            <FormNumericInput
                headingText="Maximum number of personal vehicles an employee can drive"
                min={1}
                max={50}
                selectedValue={greyFleetSettings.vehiclesPerEmployee}
                onValueChange={(value) =>
                    handleSettingsChange("vehiclesPerEmployee", value)
                }
                dangerHelperText={errors.vehiclesPerEmployee}
            />
            <FormNumericInput
                headingText="Maximum number of points allowed on licence to be able to drive on company business"
                min={0}
                max={12}
                selectedValue={greyFleetSettings.maximumPointsThreshold}
                onValueChange={(value) =>
                    handleSettingsChange("maximumPointsThreshold", value)
                }
                dangerHelperText={errors.maximumPointsThreshold}
            />
            <FormSwitch
                headingText="Allow employees to register their own vehicles"
                helperText="Employees can add their own vehicles through the Vision App. Vehicles added by an employee will need to be approved by a grey fleet administrator before any details are checked"
                checked={greyFleetSettings.employeesCanRegisterVehicles}
                onChange={(e) =>
                    handleSettingsChange(
                        "employeesCanRegisterVehicles",
                        e.target.checked
                    )
                }
                disabled={isLoading}
            />
            <h3>Employee Declaration</h3>
            <FormSwitch
                headingText="Require employee to sign a declaration"
                helperText="Allows you to add a custom declaration to verify that user submitted details such as insurance details are accurate"
                checked={greyFleetSettings.declarationRequired}
                onChange={(e) =>
                    handleSettingsChange(
                        "declarationRequired",
                        e.target.checked
                    )
                }
                disabled={isLoading}
            />
            <ShowHide
                evaluator={greyFleetSettings.declarationRequired}
                show={
                    <FormTextArea
                        id="grey-fleet-text-area"
                        headingText="Declaration Text"
                        value={greyFleetSettings.declarationText}
                        onChange={handleDeclarationTextChange}
                        maxLength={1000}
                        disabled={isLoading}
                        showCounter
                        dangerHelperText={errors.declarationText}
                    />
                }
            />
            <ButtonSave
                onClick={handleSave}
                disabled={isSaving}
                simpleDisabled={!isDataDirty || !isValid}
                loading={isLoading}
            />
            <AlertUnsavedData
                isDirty={isDataDirty}
                scrollToBottomOnCancel={true}
            />
        </div>
    );
}
